import React, { useEffect, useState, useRef, useContext } from 'react';
import { useLocation } from 'react-router';

import ReactModal from 'react-modal';
import { useModal } from 'react-modal-hook';

import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import QAForm from '../../modals/QAForm';

import { UserContext } from '../../context/AuthContext';
import { getAllComments, postComments } from '../../services/discussionForum';
import { showError, utcToLocalDateTime, showSuccess } from '../../Utils/helper';
import { fileUpload } from '../../Utils/fileUpload';

import { icons } from '../../../constants/icons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';

import { VoiceRecorder, VoiceRecorderMicButton, getFileExtFromMime } from '../../VoiceRecorder'; // ← adjust path

import './AskInstructor.css';

const VOICE_EXTENSIONS = ['.webm', '.m4a', '.aac', '.ogg'];
const isVoiceMessage = (filename) => filename && VOICE_EXTENSIONS.some((ext) => filename.endsWith(ext));

function AskInstructor({ instructor, onBack }) {
    const { t } = useTranslation();
    const { user } = useContext(UserContext);

    const [data, setData] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });

    const { id: instructor_id, full_name: instructor_name } = instructor;

    const onProgress = (progress) => {
        setUploadProgress(progress);
    };

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [show]);

    async function getComments() {
        setLoading(true);
        const resp = await getAllComments(instructor_id);
        if (resp.success) {
            setData(resp.data.reverse());
        } else {
            showError(t('alertMessage.wrong'));
        }
        setLoading(false);
    }

    useEffect(() => {
        getComments();
    }, []);

    async function sendMessage(e) {
        e.preventDefault();
        setMessage('');
        setLoading(true);
        if (message.length > 0) {
            let payload = {
                comment: message,
            };
            const resp = await postComments(instructor_id, payload);
            if (resp.success) {
                // message sent
            } else {
                showError(t('alertMessage.wrong'));
            }
            getComments();
        }
        setLoading(false);
    }

    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [data]);

    const [openQAModal, closeQAModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeQAModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <QAForm modalClose={closeQAModal} showSuccess={showSuccess} />
            </ReactModal>
        );
    });

    function handleFileChange(e) {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size / 1024 / 1024 < 30) {
            sendAttachment(file);
        } else {
            e.target.value = null;
            showError(t('alertMessage.fileNotMoreThan'));
        }
    }

    const getAttachmentUrl = (path) => {
        return process.env.REACT_APP_CONTENT_URL + path;
    };

    async function sendAttachment(file) {
        if (!file) return;
        setIsUploading(true);
        setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });

        try {
            const filePath = await fileUpload(file, 'storage/attachments', onProgress);

            let payload = {
                msg_type: 'attachment',
                comment: 'Image',
                attachment: '/' + filePath,
                file_ext: file.name.split('.').pop(),
            };

            const resp = await postComments(instructor_id, payload);
            if (resp.success) {
                getComments();
            } else {
                if (resp.status === 422) {
                    showError(t('alertMessage.fileNotMoreThan'));
                } else {
                    showError(t('alertMessage.wrong'));
                }
            }
        } catch (err) {
            console.error('Attachment upload failed:', err);
            showError(t('alertMessage.wrong'));
        } finally {
            setIsUploading(false);
            setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
        }
    }

    async function sendVoiceRecording(audioBlob) {
        const ext = getFileExtFromMime(audioBlob.type);
        const timestamp = Date.now();
        const file = new File([audioBlob], `voice-${timestamp}.${ext}`, { type: audioBlob.type });

        setIsUploading(true);
        setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });

        try {
            const filePath = await fileUpload(file, 'storage/attachments', onProgress);

            const payload = {
                msg_type: 'attachment',
                comment: 'Voice message',
                attachment: '/' + filePath,
                file_ext: ext,
            };

            const resp = await postComments(instructor_id, payload);
            if (resp.success) {
                getComments();
            } else {
                showError(t('alertMessage.wrong'));
            }
        } catch (err) {
            console.error('Voice upload failed:', err);
            showError(t('alertMessage.wrong'));
        } finally {
            setIsUploading(false);
            setIsRecording(false);
            setUploadProgress({ percent: 0, uploadedMB: 0, totalMB: 0, speedMbps: 0 });
        }
    }

    return (
        <section className="ask-instructor">
            <div className="ask-instructor__header">
                <button className="ask-instructor__back-button" onClick={onBack}>
                    <ArrowBackIcon />
                </button>
                <div className="ask-instructor__profile">
                    <div className="ask-instructor__profile-image">{instructor_name[0]}</div>
                    <h1>{instructor_name}</h1>
                </div>

                {user?.type === 'instructor' && (
                    <button className="btn btn-primary mt-3 QAButton" onClick={openQAModal}>
                        Highlight Question
                    </button>
                )}
            </div>

            <div className="msg_win">
                <>
                    {data?.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-center empty-chat">
                            <h2 className="text-center">{t('common.startConversation')}</h2>
                        </div>
                    ) : (
                        <>
                            {data &&
                                data?.map((_comment) => {
                                    return (
                                        <div key={_comment?.id}>
                                            {_comment?.user_id == instructor_id ? (
                                                <div className="ask-instructor__reply-text">
                                                    {_comment.attachment !== '' && _comment.attachment !== null ? (
                                                        <div className="msg_file">
                                                            {isVoiceMessage(_comment?.attachment?.attachment_file) ? (
                                                                <div className="msg_voice">
                                                                    <audio
                                                                        controls
                                                                        src={getAttachmentUrl(
                                                                            _comment?.attachment?.attachment_path
                                                                        )}
                                                                        style={{ height: '36px', maxWidth: '250px' }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <a
                                                                        href={getAttachmentUrl(
                                                                            _comment?.attachment?.attachment_path
                                                                        )}
                                                                        className="download_button"
                                                                        download
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <img
                                                                            src={icons.icon_download_black}
                                                                            alt="download"
                                                                        />
                                                                    </a>
                                                                    {_comment?.attachment?.attachment_file}
                                                                </>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p>{_comment?.comment}</p>
                                                    )}

                                                    <div className="reply_date">
                                                        {utcToLocalDateTime(_comment?.created_at)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="ask-instructor__send-text">
                                                    {_comment.attachment !== '' && _comment.attachment !== null ? (
                                                        <div className="msg_file">
                                                            {isVoiceMessage(_comment?.attachment?.attachment_file) ? (
                                                                <div className="msg_voice">
                                                                    <audio
                                                                        controls
                                                                        src={getAttachmentUrl(
                                                                            _comment?.attachment?.attachment_path
                                                                        )}
                                                                        style={{ height: '36px', maxWidth: '250px' }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <a
                                                                        href={getAttachmentUrl(
                                                                            _comment?.attachment?.attachment_path
                                                                        )}
                                                                        className="download_button"
                                                                        download
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        <img src={icons.icon_download} alt="download" />
                                                                    </a>
                                                                    {_comment?.attachment?.attachment_file}
                                                                </>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p>{_comment?.comment}</p>
                                                    )}
                                                    <div className="send_date">
                                                        {_comment?.human_readable_created_at}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </>
            </div>

            <div className="ask-instructor__menu">
                {isRecording ? (
                    <VoiceRecorder
                        onSend={(blob) => sendVoiceRecording(blob)}
                        onCancel={() => setIsRecording(false)}
                        isUploading={isUploading}
                    />
                ) : (
                    <div className="msg_input">
                        <form onSubmit={sendMessage} className="grey_input w-100 mt-0">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('common.typeMessage')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            />
                            <div className="attachment_button">
                                <input type="file" id="attachment" onChange={handleFileChange} />
                                <label className="form-label" htmlFor="attachment">
                                    <img src={icons.icon_attachment} alt="" />
                                </label>
                            </div>

                            <VoiceRecorderMicButton className="ml-2" onClick={() => setIsRecording(true)} />

                            <span className="ml-2 svg_icon cursor_pointer" onClick={sendMessage}>
                                <SendIcon />
                            </span>
                        </form>
                    </div>
                )}

                {isUploading && uploadProgress.percent > 0 && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '8px',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                                fontSize: '12px',
                                color: '#6b7280',
                                marginBottom: '4px',
                            }}
                        >
                            <span>
                                {uploadProgress.uploadedMB.toFixed(1)}MB / {uploadProgress.totalMB.toFixed(1)}MB @{' '}
                                {uploadProgress.speedMbps.toFixed(1)} MB/s
                            </span>
                            <span>{uploadProgress.percent}%</span>
                        </div>
                        <div
                            style={{
                                width: '100%',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '9999px',
                                height: '8px',
                            }}
                        >
                            <div
                                style={{
                                    width: `${uploadProgress.percent}%`,
                                    backgroundColor: '#3b82f6',
                                    height: '8px',
                                    borderRadius: '9999px',
                                    transition: 'width 0.3s ease',
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <ToastContainer />
        </section>
    );
}

export default AskInstructor;
